import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { Panel, SectionHead, SectionShell } from "./_shared";
import { CalendarClock } from "lucide-react";

const IST_OFFSET_MIN = 330;
function istDayBoundsISO() {
  const now = new Date();
  const ist = new Date(now.getTime() + (IST_OFFSET_MIN + now.getTimezoneOffset()) * 60000);
  ist.setHours(0, 0, 0, 0);
  const start = new Date(ist.getTime() - (IST_OFFSET_MIN + now.getTimezoneOffset()) * 60000);
  const end = new Date(start.getTime() + 24 * 3600 * 1000);
  return { start: start.toISOString(), end: end.toISOString() };
}
const STATUS_TONE = {
  completed: "border-pawkt-success-deep/40 bg-pawkt-mint text-pawkt-success-deep",
  confirmed: "border-pawkt-primary/30 bg-pawkt-primary-soft text-pawkt-primary-deep",
  pending: "border-pawkt-warning/40 bg-pawkt-butter text-pawkt-secondary-deep",
  in_progress: "border-pawkt-info/40 bg-[#EAEFFB] text-[#4C68B5]",
  cancelled: "border-pawkt-emergency/30 bg-pawkt-coral text-pawkt-emergency line-through",
  no_show: "border-pawkt-emergency/30 bg-pawkt-coral text-pawkt-emergency",
};
const STATUS_LABEL = { completed: "Completed", confirmed: "Upcoming", pending: "Pending", in_progress: "In progress", cancelled: "Cancelled", no_show: "No show" };
const HOURS = [8, 10, 12, 14, 16, 18, 20];

function useNarrow(max = 430) {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const m = window.matchMedia(`(max-width: ${max}px)`);
    const on = () => setNarrow(m.matches);
    on();
    m.addEventListener?.("change", on);
    return () => m.removeEventListener?.("change", on);
  }, [max]);
  return narrow;
}

export function BusinessToday() {
  const navigate = useNavigate();
  const narrow = useNarrow(430);
  const { start, end } = istDayBoundsISO();
  const q = useQuery({
    queryKey: ["today-bookings"],
    queryFn: async () => (await api.get(`/bookings?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&limit=100`)).data,
  });

  const hourOf = (iso) => {
    const d = new Date(iso);
    return (d.getUTCHours() + 5) % 24 + (d.getUTCMinutes() + 30) / 60;
  };
  const pos = (iso) => Math.max(0, Math.min(100, ((hourOf(iso) - 8) / 12) * 100));
  const timeLabel = (iso) => {
    const d = new Date(iso);
    let h = (d.getUTCHours() + 5) % 24, m = d.getUTCMinutes() + 30;
    if (m >= 60) { m -= 60; h = (h + 1) % 24; }
    const ap = h >= 12 ? "PM" : "AM"; const hh = ((h + 11) % 12) + 1;
    return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
  };

  return (
    <Panel className="p-5 sm:p-6" data-testid="business-today">
      <SectionHead eyebrow="Your Business Today" title="The day at a glance"
        action={<button onClick={() => navigate("/calendar")} className="text-xs font-bold text-pawkt-primary-deep hover:underline">Open calendar →</button>} />
      <SectionShell query={q}>
        {(d) => {
          const items = (d.items || []).filter((b) => b.start_time).sort((a, b) => a.start_time.localeCompare(b.start_time));
          if (items.length === 0) {
            return (
              <div className="rounded-2xl border border-pawkt-border-soft bg-pawkt-soft px-5 py-8 text-center" data-testid="today-empty">
                <CalendarClock className="mx-auto h-8 w-8 text-pawkt-primary/60" />
                <p className="mt-2 font-display text-base font-bold text-pawkt-text">A quiet day.</p>
                <p className="text-xs text-pawkt-text-secondary">No bookings are scheduled yet.</p>
                <button onClick={() => navigate("/bookings")} className="mt-3 inline-flex items-center rounded-full bg-primary px-4 py-2 text-xs font-bold text-white shadow-purpleGlow hover:bg-pawkt-primary-hover">
                  Create booking
                </button>
              </div>
            );
          }

          // Compact chronological list for narrow viewports (<=430px)
          if (narrow) {
            return (
              <div data-testid="today-list">
                <div className="divide-y divide-pawkt-divider">
                  {items.slice(0, 14).map((b) => (
                    <button key={b.id} onClick={() => navigate("/bookings")} data-testid={`today-booking-${b.id}`}
                      className="flex w-full items-center gap-3 py-2.5 text-left">
                      <span className="w-16 shrink-0 text-xs font-bold tabular text-pawkt-text-secondary">{timeLabel(b.start_time)}</span>
                      <span className="min-w-0 flex-1 truncate text-sm font-bold text-pawkt-text">
                        {b.service_name || "Service"}{b.pet_name ? ` · ${b.pet_name}` : ""}
                      </span>
                      <span className={cn("shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold", STATUS_TONE[b.status] || "border-pawkt-border bg-white text-pawkt-text")}>
                        {STATUS_LABEL[b.status] || b.status}
                      </span>
                    </button>
                  ))}
                </div>
                {items.length > 14 && (
                  <button onClick={() => navigate("/bookings")} className="mt-3 text-xs font-bold text-pawkt-primary-deep hover:underline">
                    +{items.length - 14} more today →
                  </button>
                )}
              </div>
            );
          }

          // Visual timeline for >430px
          return (
            <div data-testid="today-timeline">
              <div className="relative h-5 mb-1">
                {HOURS.map((h) => (
                  <span key={h} className="absolute -translate-x-1/2 text-[10px] font-semibold text-pawkt-text-disabled" style={{ left: `${((h - 8) / 12) * 100}%` }}>
                    {((h + 11) % 12) + 1}{h >= 12 ? "p" : "a"}
                  </span>
                ))}
              </div>
              <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
                {items.slice(0, 14).map((b) => (
                  <div key={b.id} className="relative h-9">
                    <div className="absolute inset-y-0 left-0 right-0 rounded-full bg-pawkt-soft border border-pawkt-border-soft" />
                    <button onClick={() => navigate("/bookings")} data-testid={`today-booking-${b.id}`}
                      className={cn("absolute inset-y-0 flex items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-bold max-w-[62%] hover:brightness-95 transition",
                        STATUS_TONE[b.status] || "border-pawkt-border bg-white text-pawkt-text")}
                      style={{ left: `${pos(b.start_time)}%` }}>
                      <span className="opacity-70">{timeLabel(b.start_time)}</span>
                      <span className="truncate">{b.service_name || "Service"}{b.pet_name ? ` · ${b.pet_name}` : ""}</span>
                    </button>
                  </div>
                ))}
              </div>
              {items.length > 14 && (
                <button onClick={() => navigate("/bookings")} className="mt-3 text-xs font-bold text-pawkt-primary-deep hover:underline">
                  +{items.length - 14} more today →
                </button>
              )}
            </div>
          );
        }}
      </SectionShell>
    </Panel>
  );
}
